import { CopyButton } from "@components/shared/CopyButton";
import {
  CANDIDATE_TOKEN_PATTERN,
  GENERIC_TOKEN_PATTERN,
  textContainsTokens,
  useTokenResolver,
} from "@hooks/useTokenResolver";
import { formatBytes } from "@lib/formatBytes";
import { useChatStore } from "@lib/stores/chatStore";
import type {
  ChatAttachment,
  ChatMessage as ChatMessageType,
  ChatRichTextArtefact,
  TokenResolutions,
} from "@lib/types";
import { AlertCircle, Bot, ChevronDown, ChevronRight, FileText, User } from "lucide-react";
import { type AnchorHTMLAttributes, useRef, useState } from "react";
import Markdown, { type Components } from "react-markdown";
import { Link } from "react-router";
import remarkGfm from "remark-gfm";
import { PlanCard } from "./PlanCard";
import { QuestionCard } from "./QuestionCard";

interface ChatMessageProps {
  message: ChatMessageType;
}

// An href points inside the app when it is a root-relative path ("/campaigns/…")
// or a pure anchor ("#section"). Tool deep_links are always root-relative.
function isInternalHref(href: string): boolean {
  return href.startsWith("/") || href.startsWith("#");
}

// Render markdown links: internal routes (tool deep_links) become react-router
// <Link> navigations so the chat does not trigger a full-page reload, and any
// #anchor is preserved. External links open in a new tab as before.
function MarkdownAnchor({ href, children }: AnchorHTMLAttributes<HTMLAnchorElement>) {
  if (href && isInternalHref(href)) {
    return <Link to={href}>{children}</Link>;
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

const ASSISTANT_MARKDOWN_COMPONENTS: Components = {
  a: MarkdownAnchor,
};

// Belt-and-braces: assistant text is restored server-side, so it normally
// carries real names. If a token survived (e.g. a binding lost before
// restore), resolve it at render time — candidate tokens become candidate
// links via the markdown pipeline; unresolvable tokens stay literal.
function resolveTokensToMarkdown(text: string, resolutions: TokenResolutions): string {
  return text
    .replace(GENERIC_TOKEN_PATTERN, (match, label) => resolutions.generic[label] ?? match)
    .replace(CANDIDATE_TOKEN_PATTERN, (match) => {
      const candidate = resolutions.candidates[match];
      // The token is `C{reference_number}`, so the number rides alongside the
      // name without a payload change: C5 -> "Jane Doe (#5)".
      return candidate ? `[${candidate.full_name} (#${match.slice(1)})](${candidate.path})` : match;
    });
}

function RichTextArtefactPreview({ artefact }: { artefact: ChatRichTextArtefact }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="flex flex-col gap-1 w-full">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-white/20 bg-white/10 text-xs max-w-[260px] hover:bg-white/20 self-start"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown size={12} className="flex-shrink-0" />
        ) : (
          <ChevronRight size={12} className="flex-shrink-0" />
        )}
        <FileText size={14} className="flex-shrink-0" />
        <span className="truncate">
          {artefact.label} · {formatBytes(artefact.size_bytes)}
        </span>
      </button>
      {expanded && (
        <div className="rounded-md border border-white/20 bg-white/10 p-3 text-xs prose prose-sm prose-invert max-w-none">
          <Markdown remarkPlugins={[remarkGfm]}>{artefact.markdown}</Markdown>
        </div>
      )}
    </div>
  );
}

function AttachmentPreview({ attachment }: { attachment: ChatAttachment }) {
  const isImage = attachment.content_type.startsWith("image/");
  const src = attachment.url || attachment.preview;

  if (isImage && src) {
    return (
      <a href={src} target="_blank" rel="noopener noreferrer" className="block">
        <img
          src={src}
          alt={attachment.filename}
          className="max-w-[200px] max-h-[150px] rounded-md object-cover border border-white/20"
        />
      </a>
    );
  }

  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-white/20 bg-white/10 text-xs max-w-[180px]">
      <FileText size={14} className="flex-shrink-0" />
      <span className="truncate">{attachment.filename}</span>
    </div>
  );
}

const CHAT_COPY_BUTTON_CLASS =
  "flex-shrink-0 self-start rounded p-1 text-text-tertiary opacity-0 transition-opacity hover:text-text-primary group-hover:opacity-100";

export function ChatMessage({ message }: ChatMessageProps) {
  const textRef = useRef<HTMLDivElement>(null);

  const metadata = message.metadata as Record<string, unknown> | null | undefined;
  const metadataKind = metadata?.kind as string | undefined;
  const planId = metadata?.plan_id as string | undefined;
  const questionId = metadata?.question_id as string | undefined;

  const activePlan = useChatStore((s) => s.plansByThread[message.thread_id] ?? null);
  const pendingQuestion = useChatStore((s) => s.questionsByThread[message.thread_id] ?? null);
  const retryMessage = useChatStore((s) => s.retryMessage);

  const isAssistantText = message.role === "assistant" && !message.tool_use && !message.tool_result;
  const needsTokenResolution = isAssistantText && textContainsTokens(message.content);
  const { resolutions } = useTokenResolver(needsTokenResolution ? message.thread_id : undefined);

  if (message.role === "system") {
    return (
      <div className="flex justify-center py-1">
        <span className="text-xs text-text-tertiary italic">{message.content}</span>
      </div>
    );
  }

  if (metadataKind === "plan_proposal" && planId) {
    if (activePlan && activePlan.id === planId) {
      return <PlanCard plan={activePlan} />;
    }
    return (
      <div className="rounded-lg border border-accent/40 bg-surface-primary p-3 text-sm">
        <div className="text-xs font-medium uppercase tracking-wide text-text-tertiary">Plan</div>
        <div className="text-text-secondary">{message.content}</div>
      </div>
    );
  }

  if (metadataKind === "user_question" && questionId) {
    if (pendingQuestion && pendingQuestion.id === questionId) {
      return <QuestionCard question={pendingQuestion} />;
    }
    return (
      <div className="rounded-lg border border-accent/40 bg-surface-primary p-3 text-sm">
        <div className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
          Quick question
        </div>
        <div className="text-text-secondary">{message.content}</div>
      </div>
    );
  }

  // tool_use rows are not rendered persistently — they read as noise in the
  // transcript ("Looking up that candidate...", "Looking up that candidate...",
  // ...). The live tool label is surfaced once at the bottom of the thread
  // via the activity indicator while the tool is in flight.
  if (message.tool_use) {
    return null;
  }

  if (message.tool_result) {
    return null; // Tool results are internal; don't render them
  }

  const isUser = message.role === "user";
  const hasAttachments = message.attachments && message.attachments.length > 0;
  const hasRichTextArtefacts =
    message.rich_text_artefacts && message.rich_text_artefacts.length > 0;
  const isPlaceholderContent =
    message.content.startsWith("[Attached") || message.content === "[Pasted document]";
  const isFailed = isUser && message.status === "failed";

  const body = (
    <div className={`group flex gap-2 ${isUser ? "flex-row-reverse" : ""}`}>
      <div
        className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
          isUser ? "bg-accent text-white" : "bg-surface-secondary text-text-secondary"
        }`}
      >
        {isUser ? <User size={14} /> : <Bot size={14} />}
      </div>
      <div
        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
          isUser
            ? "bg-accent text-white rounded-tr-none"
            : "bg-surface-secondary text-text-primary rounded-tl-none prose prose-sm prose-neutral dark:prose-invert max-w-none chat-prose"
        }`}
      >
        {hasAttachments && (
          <div className="flex flex-wrap gap-2 mb-2">
            {message.attachments!.map((att) => (
              <AttachmentPreview key={att.signed_id || att.filename} attachment={att} />
            ))}
          </div>
        )}
        {hasRichTextArtefacts && (
          <div className="flex flex-col gap-2 mb-2">
            {message.rich_text_artefacts!.map((artefact) => (
              <RichTextArtefactPreview key={artefact.id} artefact={artefact} />
            ))}
          </div>
        )}
        <div ref={textRef}>
          {isUser ? (
            isPlaceholderContent ? null : (
              <span className="whitespace-pre-wrap">{message.content}</span>
            )
          ) : (
            <Markdown remarkPlugins={[remarkGfm]} components={ASSISTANT_MARKDOWN_COMPONENTS}>
              {needsTokenResolution
                ? resolveTokensToMarkdown(message.content, resolutions)
                : message.content}
            </Markdown>
          )}
        </div>
      </div>
      <CopyButton
        ariaLabel="Copy message"
        className={CHAT_COPY_BUTTON_CLASS}
        getContent={() => {
          const el = textRef.current;
          if (!el) return { html: "", text: "" };
          return {
            html: el.innerHTML,
            text: el.innerText || el.textContent || "",
          };
        }}
      />
    </div>
  );

  if (!isFailed) return body;

  // The optimistic send failed and never persisted. Keep the user's message
  // visible (so they can see what they typed) and surface a working retry.
  return (
    <div className="flex flex-col gap-1">
      {body}
      <div className="flex items-center justify-end gap-2 pr-8 text-xs text-error">
        <AlertCircle size={12} className="flex-shrink-0" />
        <span>Failed to send</span>
        <button
          type="button"
          onClick={() => retryMessage(message.id)}
          className="font-medium underline hover:text-text-primary"
        >
          Retry
        </button>
      </div>
    </div>
  );
}
